import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { ButtonGroupRow } from "@tento-ui/components/button-group-row";
import { ProposalDetail } from "./proposal-detail";

/**
 * Agent-generated scheduling proposals. A segmented pill bar
 * (ButtonGroupRow) selects among plans; a ProposalDetail card below
 * shows the rationale, assignment table, and CTA for the active plan.
 *
 *   ┌─────────────────────────────────────────────────┐
 *   │  [ A · Morning ]  [ B · Spread ]  [ C · PM ]   │
 *   │                                                 │
 *   │  ┌───────────────────────────────────────────┐  │
 *   │  │ Frontloads the hardest work…              │  │
 *   │  │                                           │  │
 *   │  │ T-12  Write quarterly report  Mon 9–10a   │  │
 *   │  │ T-14  Review PR #42          Mon 10–10:30 │  │
 *   │  │ T-15  Plan onboarding deck   Mon 11–12p   │  │
 *   │  │                                           │  │
 *   │  │                  [ Schedule plan A ]       │  │
 *   │  └───────────────────────────────────────────┘  │
 *   └─────────────────────────────────────────────────┘
 *
 * Zero lona-js dependencies. Lives in tento/tento-ui-js.
 */
@component({ name: "std-proposal-list" })
export class ProposalList extends LonaWebComponent {
  private $root: HTMLElement = this.$("root");

  private proposals: ProposalList.Proposal[] = [];
  private selectedIndex: number = 0;
  private delegate: Option<ProposalList.Delegate> = null;

  private $group: Option<ButtonGroupRow<string>> = null;
  private $detail: Option<ProposalDetail> = null;

  static makeWith(props: ProposalList.Props): ProposalList {
    return ProposalList.make().bind(props);
  }

  bind(props: ProposalList.Props): this {
    this.proposals = props.proposals;
    this.delegate = props.delegate ?? null;
    this.selectedIndex = props.selectedIndex ?? 0;
    this.render();
    return this;
  }

  getSelectedIndex(): number {
    return this.selectedIndex;
  }

  setSelectedIndex(index: number): void {
    if (index === this.selectedIndex) return;
    if (index < 0 || index >= this.proposals.length) return;
    this.selectedIndex = index;
    this.$group?.bindSelected(String(index));
    this.rebindDetail();
    this.delegate?.onProposalFocus?.(index);
  }

  // ── rendering ──────────────────────────────────────────────────

  private render(): void {
    DomUtils.clearChildren(this.$root);
    this.$detail = null;
    if (this.proposals.length === 0) return;

    // ── pill bar ──
    this.$group = ButtonGroupRow.makeWith({
      selected: String(this.selectedIndex),
      entries: this.proposals.map((p, i) => ({
        identifier: String(i),
        label: p.label,
      })),
      onSelected: (id) => {
        const idx = Number.parseInt(id, 10);
        if (Number.isNaN(idx)) return;
        this.selectedIndex = idx;
        this.rebindDetail();
        this.delegate?.onProposalFocus?.(idx);
      },
    });
    this.$root.appendChild(this.$group);

    // ── detail card ──
    this.$detail = ProposalDetail.make();
    this.$root.appendChild(this.$detail);
    this.rebindDetail();
  }

  private rebindDetail(): void {
    const proposal = this.proposals[this.selectedIndex];
    if (!proposal || !this.$detail) return;
    this.$detail.bind({
      rationale: proposal.rationale,
      assignments: proposal.assignments,
      actionLabel: "Select",
      onSelect: () => this.delegate?.onSelect?.(this.selectedIndex),
    });
  }

  // ── lifecycle ──────────────────────────────────────────────────

  onRecycle(): void {
    super.onRecycle?.();
    this.proposals = [];
    this.delegate = null;
    this.selectedIndex = 0;
    this.$group = null;
    this.$detail = null;
  }

  // ── factory ────────────────────────────────────────────────────

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
      }

      #root {
        gap: 4px;
      }
    `,
  ];

  static $html = html`
    <std-col id="root"></std-col>
  `;
}

export namespace ProposalList {
  export interface Assignment {
    taskId: string;
    displayId: string;
    title: string;
    timeLabel: string;
  }

  export interface Proposal {
    id: string;
    label: string;
    rationale?: Option<string>;
    assignments: Assignment[];
  }

  export interface Delegate {
    onProposalFocus?: (proposalIndex: number) => void;
    onSelect?: (proposalIndex: number) => void;
  }

  export type Props = {
    proposals: Proposal[];
    selectedIndex?: Option<number>;
    delegate?: Option<Delegate>;
  };
}
